import React, { useState, useMemo } from 'react';
import { IGetBPAShort } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { ISelectOption } from 'components/Select/types';
import { getCustomerBPAShort } from 'backend/customers';
import FormSelect from 'components/Select/FormSelect';
import Text from 'components/Text/Text';
import Button from 'components/Button/Button';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import classNames from 'classnames';
import style from './Bpa.module.scss';

interface IProps {
	setBpa: (value: Nullable<string>) => void;
	IDSelectedCustomer: Nullable<string>;
	bpaSetId: Nullable<string>;
}

export const Bpa: React.FC<IProps> = ({ setBpa, IDSelectedCustomer, bpaSetId }) => {
	const [isOpenFields, setIsOpenFields] = useState(false);
	const expandIconClasses = classNames(style.expandIcon, { [style.rotated]: isOpenFields });
	const expandFieldsClasses = classNames({ [style.collapsed]: !isOpenFields });

	const { response, responseError, refetch, loading } = useFetchData<IGetBPAShort, string>(
		IDSelectedCustomer ? getCustomerBPAShort : null,
		IDSelectedCustomer || '',
	);

	const mappedOptions = useMemo<ISelectOption[]>(() => {
		return response
			? response.data.bpaSets.map((bpaSet) => ({ id: bpaSet.id, name: bpaSet.name || 'no name' }))
			: [];
	}, [response]);

	const bpaData = useMemo<Nullable<IGetBPAShort['data']['bpaSets'][0]>>(() => {
		return response?.data.bpaSets.find((bpaSet) => bpaSet.id === bpaSetId) || null;
	}, [response, bpaSetId]);

	return (
		<>
			<FormSelect
				testId="Bpa"
				name="bpaSetId"
				label="setLabel"
				options={mappedOptions}
				loading={loading}
				fetchError={responseError}
				onRefetch={refetch}
				onChange={setBpa}
			/>
			{bpaData && (
				<div className={style.bpaFields}>
					<Button
						className={style.expandButton}
						theme="secondaryPurple"
						onClick={() => setIsOpenFields((state) => !state)}
						icon={
							<svg className={expandIconClasses}>
								<use xlinkHref={`${breadcrumbs}#rightAngleArrow`} />
							</svg>
						}
						data-test="expandBPAFieldsButton">
						<Text
							translationNameSpace="form"
							translationKey={isOpenFields ? 'hideAddedFieldsButton' : 'expandAddedFieldsButton'}
							asFragment={true}
						/>
					</Button>
					<div className={expandFieldsClasses}>
						<div className={style.row}>
							<FullInfoField label="recipientAccountNumberLabel" value={bpaData.recipientAccountNumber} />
							<FullInfoField label="recipientNameLabel" value={bpaData.recipientName} />
						</div>
						<div className={style.row}>
							<FullInfoField label="recipientBankLabel" value={bpaData.recipientBank} />
							<FullInfoField label="recipientBankCityLabel" value={bpaData.recipientBankCity} />
						</div>
						<div className={style.row}>
							<FullInfoField label="recipientInnLabel" value={bpaData.recipientInn} />
							<FullInfoField label="recipientBicLabel" value={bpaData.recipientBic} />
						</div>
						<div className={style.row}>
							<FullInfoField
								label="recipientCorrespondentAccountLabel"
								value={bpaData.recipientCorrespondentAccount}
							/>
							<FullInfoField label="paymentDetailsLabel" value={bpaData.paymentDetails} />
						</div>
						<div className={style.row}>
							<FullInfoField label="cardContractNumberLabel" value={bpaData.cardContractNumber} />
							<FullInfoField label="sbpContractNumberLabel" value={bpaData.sbpContractNumber} />
						</div>
					</div>
				</div>
			)}
		</>
	);
};
